import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Base_URL } from "../url";

// InventoryStatusScreen
// - Fetches inventory + item master
// - Joins on itemCode to show itemName
// - Hides `id`
// - Client-side search/sort/CSV

export default function InventoryStatusScreen() {
  const [items, setItems] = useState([]);              // raw inventory
  const [itemMap, setItemMap] = useState({});          // { itemCode: itemName }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const invUrl = `${Base_URL}/Inventory/getInventory`;
        const itmUrl = `${Base_URL}/Item/ItemGetData`;

        const [invRes, itmRes] = await Promise.all([axios.get(invUrl), axios.get(itmUrl)]);

        if (!mounted) return;

        // Inventory list
        const inv = Array.isArray(invRes.data) ? invRes.data : invRes.data || [];
        setItems(inv);

        // Build itemCode -> itemName map
        const arr = Array.isArray(itmRes.data) ? itmRes.data : itmRes.data || [];
        const map = {};
        for (const r of arr) {
          if (r?.itemCode) map[r.itemCode] = r.itemName ?? "";
        }
        setItemMap(map);
      } catch (err) {
        setError(err?.response?.data || err.message || "Failed to fetch data");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, []);

  // Enrich inventory rows with itemName from the map
  const joined = useMemo(
    () =>
      items.map((r) => ({
        ...r,
        itemName: itemMap[r?.itemCode] ?? "", // add itemName field
      })),
    [items, itemMap]
  );

  // Columns to show (do NOT include `id`)
  const columns = useMemo(
    () => [
      { key: "officeCode", label: "Office" },
      { key: "storageCenter", label: "Storage Center" },
      { key: "itemCode", label: "Item Code" },
      { key: "itemName", label: "Item Name" }, // <-- new
      { key: "unit", label: "Unit" },
      { key: "itemQty", label: "Qty" },
      { key: "itemPrice", label: "Price" },
      { key: "itemValue", label: "Value" },
      { key: "storeCode", label: "Store" },
    ],
    []
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = joined.slice();

    if (q) {
      list = list.filter((r) => {
        return (
          String(r.itemCode || "").toLowerCase().includes(q) ||
          String(r.itemName || "").toLowerCase().includes(q) || // search by name
          String(r.officeCode || "").toLowerCase().includes(q) ||
          String(r.storageCenter || "").toLowerCase().includes(q) ||
          String(r.comapnyCode || "").toLowerCase().includes(q)
        );
      });
    }

    if (sortBy) {
      list.sort((a, b) => {
        const A = a[sortBy];
        const B = b[sortBy];
        if (A == null && B == null) return 0;
        if (A == null) return sortDir === "asc" ? -1 : 1;
        if (B == null) return sortDir === "asc" ? 1 : -1;
        if (typeof A === "number" && typeof B === "number") return sortDir === "asc" ? A - B : B - A;
        return sortDir === "asc"
          ? String(A).localeCompare(String(B))
          : String(B).localeCompare(String(A));
      });
    }

    return list;
  }, [joined, search, sortBy, sortDir]);

  function toggleSort(colKey) {
    if (sortBy === colKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(colKey);
      setSortDir("asc");
    }
  }

  function exportCSV() {
    if (!filtered.length) return;
    const header = columns.map((c) => c.label);
    const rows = filtered.map((r) =>
      columns.map((c) => {
        const v = r[c.key];
        if ((c.key === "createdAt" || c.key === "updatedAt") && v) {
          try {
            return new Date(v).toLocaleString();
          } catch {
            return v;
          }
        }
        return v ?? "";
      })
    );

    const csvContent = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inventory_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Inventory Status</h2>
        <div className="flex gap-2">
          <input
            className="border rounded px-2 py-1"
            placeholder="Search item code/name, office..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className="bg-blue-600 text-white px-3 py-1 rounded"
            onClick={exportCSV}
            disabled={!filtered.length}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-auto border rounded">
        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : error ? (
          <div className="p-6 text-red-600">Error: {String(error)}</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-3 py-2 text-left cursor-pointer select-none"
                    onClick={() => toggleSort(col.key)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{col.label}</span>
                      {sortBy === col.key ? <span>{sortDir === "asc" ? "▲" : "▼"}</span> : null}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="p-6 text-center text-gray-500">
                    No records found
                  </td>
                </tr>
              ) : (
                filtered.map((row, idx) => (
                  <tr key={row.id ?? `${row.itemCode}-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-2 align-top">
                        {col.key === "createdAt" || col.key === "updatedAt"
                          ? row[col.key]
                            ? new Date(row[col.key]).toLocaleString()
                            : ""
                          : (row[col.key] ?? "").toString()}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
